import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

interface PWAContextValue {
  isInstalled: boolean;
  isInstallable: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isStandalone: boolean;
  installPrompt: () => Promise<void>;
  dismissInstall: () => void;
  canShowInstall: boolean;
  showIOSInstructions: boolean;
  setShowIOSInstructions: (show: boolean) => void;
  isPushSupported: boolean;
  isPushSubscribed: boolean;
  subscribeToPush: () => Promise<void>;
  unsubscribeFromPush: () => Promise<void>;
}

const PWAContext = createContext<PWAContextValue | null>(null);

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within PWAProvider');
  }
  return context;
};

export const PWAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [canShowInstall, setCanShowInstall] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isPushSupported, setIsPushSupported] = useState(false);
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);

  // Detect platform
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);

  // Check if running as standalone PWA
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;

  useEffect(() => {
    // Check if already installed
    if (isStandalone) {
      setIsInstalled(true);
      setCanShowInstall(false);
      return;
    }

    // Check localStorage for dismissal
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const dismissedDate = new Date(dismissedTime);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        setCanShowInstall(false);
        return;
      }
    }

    // Check engagement criteria (stored in localStorage)
    const chatCount = parseInt(localStorage.getItem('chat-interaction-count') || '0');
    if (chatCount >= 2) {
      setCanShowInstall(true);
    }

    // Listen for beforeinstallprompt event (Chrome/Edge/Samsung)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);

      // Only show if engagement criteria met
      if (chatCount >= 2) {
        setCanShowInstall(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      console.log('PWA was installed');
      setIsInstalled(true);
      setIsInstallable(false);
      setCanShowInstall(false);
      setDeferredPrompt(null);
    });

    // For iOS, check if it's installable but not installed
    if (isIOS && !isStandalone) {
      // Check if iOS version supports PWA (16.4+)
      const iOSVersion = navigator.userAgent.match(/OS (\d+)_/);
      if (iOSVersion && parseInt(iOSVersion[1]) >= 16) {
        setIsInstallable(true);
        if (chatCount >= 2) {
          setCanShowInstall(true);
        }
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isStandalone, isIOS]);

  const installPrompt = useCallback(async () => {
    if (isIOS) {
      // Show iOS instructions modal
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) {
      console.log('Install prompt not available');
      return;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to install prompt: ${outcome}`);

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setIsInstalled(true);
      } else {
        console.log('User dismissed the install prompt');
        // Don't show again for 7 days
        localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
        setCanShowInstall(false);
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
  }, [deferredPrompt, isIOS]);

  const dismissInstall = useCallback(() => {
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
    setCanShowInstall(false);
  }, []);

  // Check for push notification support
  useEffect(() => {
    const checkPushSupport = async () => {
      if ('Notification' in window && 'PushManager' in window && 'serviceWorker' in navigator) {
        setIsPushSupported(true);

        // Check if already subscribed
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsPushSubscribed(!!subscription);
        } catch (error) {
          console.error('Error checking push subscription:', error);
        }
      }
    };

    checkPushSupport();
  }, []);

  const subscribeToPush = useCallback(async () => {
    if (!isPushSupported) {
      console.log('Push notifications not supported');
      return;
    }

    try {
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Push notification permission denied');
        return;
      }

      // Get VAPID public key from backend
      const { data } = await axios.get('/push/vapid-public-key');
      const vapidPublicKey = data.publicKey;

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      // Send subscription to backend
      const token = localStorage.getItem('accessToken');
      await axios.post('/push/subscribe', subscription.toJSON(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsPushSubscribed(true);
      console.log('Push notification subscription successful');
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
    }
  }, [isPushSupported]);

  const unsubscribeFromPush = useCallback(async () => {
    if (!isPushSupported || !isPushSubscribed) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        // Notify backend
        const token = localStorage.getItem('accessToken');
        await axios.delete(
          `/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setIsPushSubscribed(false);
        console.log('Push notification unsubscribe successful');
      }
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
    }
  }, [isPushSupported, isPushSubscribed]);

  const value: PWAContextValue = {
    isInstalled,
    isInstallable,
    isIOS,
    isAndroid,
    isStandalone,
    installPrompt,
    dismissInstall,
    canShowInstall,
    showIOSInstructions,
    setShowIOSInstructions,
    isPushSupported,
    isPushSubscribed,
    subscribeToPush,
    unsubscribeFromPush,
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
};

// Utility function to track user engagement
export const trackChatInteraction = () => {
  const count = parseInt(localStorage.getItem('chat-interaction-count') || '0');
  localStorage.setItem('chat-interaction-count', (count + 1).toString());
};

// Utility function to register service worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
      });

      console.log('Service Worker registered:', registration);

      // Listen for force-reload messages from service worker (stale bundle detection)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'FORCE_RELOAD') {
          console.log('[SW] Force reload — stale bundle detected');
          window.location.reload();
        }
      });

      // Check for updates periodically
      setInterval(
        () => {
          registration.update();
        },
        60 * 60 * 1000,
      ); // Check every hour

      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker available, show update prompt
              if (window.confirm('A new version is available! Reload to update?')) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              }
            }
          });
        }
      });

      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  throw new Error('Service Workers not supported');
};
